<script lang="ts">
  import {
    avatarImageUrl,
    avatarThumbUrl,
    cn,
    firstGrapheme,
    getInitials,
    nameToColor,
    presetAvatarThumbUrl,
    resolveAvatarSource,
  } from "$lib/utils.js";
  import { client } from "$lib/state/client.js";
  import { CLOUD_RELAY_HTTP_BASE } from "$lib/core/relay-config.js";
  import { mediaToken } from "$lib/media/asset-url.svelte.js";
  import { connectOrgFor } from "$lib/state/connect.svelte.js";
  import { coldStartTrace } from "$lib/desktop-cold-start-trace.js";

  let {
    name,
    width = 36,
    size,
    fontSize,
    fontWeight = 600,
    borderRadius = "50%",
    image,
    avatar,
    overdueWarning = false,
    status = null,
    userId = null,
    traceAs,
    class: className = "",
  }: {
    name: string;
    width?: number;
    /**
     * Optional named size token. When provided it overrides `width` with a
     * fixed pixel value; when omitted, the numeric `width` prop is used as-is
     * (backward-compatible). Mapping (matches Mattermost xs..xl + our usage):
     *   xs = 20px, sm = 24px, md = 36px (default width), lg = 48px, xl = 72px
     */
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    fontSize?: number;
    fontWeight?: number | string;
    /**
     * LOGOS ONLY (workspace / org marks). Every person and every agent is a
     * circle — one shape for both, so a row never reads differently depending on
     * who is on the other end. Callers are pinned by avatar-shape.source.test.ts.
     */
    borderRadius?: number | string;
    image?: string | null;
    /**
     * Raw avatar string that may be an image URL OR a single emoji (cybo
     * avatars). When provided it is resolved via the ONE shared
     * `resolveAvatarSource` rule, so Avatar renders the emoji branch natively
     * instead of each cybo surface re-deciding with its own regex. Mutually
     * exclusive with `image` in practice; if both are passed, `avatar` wins.
     * Omitting it keeps the legacy `image` + initials behavior unchanged.
     */
    avatar?: string | null;
    overdueWarning?: boolean;
    /**
     * Optional presence indicator. When set (non-null) a small dot is rendered
     * at the bottom-right of the avatar. When null/undefined nothing extra is
     * rendered (current behavior — callers opt in).
     */
    status?: "online" | "away" | "offline" | "error" | null;
    /**
     * Who this avatar depicts. Used for ONE thing: resolving the Cyborg7 Connect
     * organization mark (the corner square that says "this person is from Acme"). Pass it
     * from every surface that knows the id — message rows, member lists, mention
     * autocomplete, search results, DM rows, the profile card — and the mark propagates
     * from this one implementation instead of a dozen chips.
     *
     * NOT the bridge-guest badge. A `slack:`/`teams:` guest is a different kind of
     * outsider and keeps ExternalGuestBadge beside their NAME (connectOrgFor refuses
     * them); the two markers can never appear together.
     */
    userId?: string | null;
    /** Desktop cold-start trace label (e.g. "rail-logo"); unset = never traced. */
    traceAs?: string;
    class?: string;
  } = $props();

  // The partner organization, or null for a host member (the overwhelming case, and it
  // costs one object lookup). Agents and webhooks never pass a userId.
  const externalOrg = $derived(userId ? connectOrgFor(userId) : null);

  /** Named size token → fixed pixel width. */
  const SIZE_TOKENS = { xs: 20, sm: 24, md: 36, lg: 48, xl: 72 } as const;

  // Effective avatar width: named `size` token wins, else the numeric `width`.
  const effectiveWidth = $derived(size ? SIZE_TOKENS[size] : width);

  // Presence dot color, matching the presence-dot classes used in ChannelSidebar.
  // Away and offline share one grey treatment (away = manual toggle OR app
  // closed); only "active" is green. No DND.
  // Solid fills — the away/offline dot was bg-content-dim/40 (40% opacity), so
  // the avatar + background bled through and it read as a half-painted dot.
  const STATUS_COLORS = {
    online: "bg-online",
    away: "bg-content-dim",
    offline: "bg-content-dim",
    // Agent presence only (ChannelSidebar's agent dot: lifecycle error is red).
    error: "bg-error",
  } as const;

  // When a raw `avatar` string is supplied, resolve it through the ONE shared
  // rule (image | emoji | initials). Otherwise keep the legacy contract: the
  // `image` prop is the URL and absence of it falls through to initials —
  // identical to the pre-emoji behavior, so existing callers are untouched.
  const source = $derived(avatar != null ? resolveAvatarSource(avatar, name) : null);
  const resolvedImage = $derived(source ? (source.kind === "image" ? source.value : null) : image);
  const emoji = $derived(source?.kind === "emoji" ? source.value : null);

  // For S3 shared-assets avatars, render a right-sized WebP thumbnail through the
  // relay thumbnailer instead of the full-res original (a 2MB agent PNG → a
  // ~15KB 360px WebP). Non-S3 URLs (bundled /agent-avatars presets, data: URIs)
  // return null and render untouched. Show the thumb first; if the thumbnailer
  // can't serve it (503/404), fall back to the raw URL rather than a broken
  // image — only if THAT also fails do we drop to the emoji/initials glyph.
  // Absolute relay origin — client.httpBase is the CURRENT connected relay
  // (runtime-correct for self-host + relay switching); CLOUD_RELAY_HTTP_BASE
  // covers the pre-connect window when client.url is still "". MUST be absolute:
  // a relative /api/... resolves to the cyborg://app shell on desktop, not the
  // relay (see avatarThumbUrl doc).
  // CYBORG-897: the 4th argument is the short-lived media pass the thumbnailer
  // REQUIRES. `mediaToken()` reads the
  // ONE app-wide cached pass and kicks a single shared mint when it is stale, so
  // a screen full of avatars costs one POST, not one per avatar. It returns null
  // until that mint lands, and the URL built without `?t=` is REFUSED (401) — which
  // is why the reset effect below re-arms on the url, not on the avatar string.
  // A full bundled portrait rendered small takes its 192px thumb copy (CYBORG-1195).
  const thumbSrc = $derived(
    resolvedImage
      ? (avatarThumbUrl(
          resolvedImage,
          effectiveWidth,
          client.httpBase || CLOUD_RELAY_HTTP_BASE,
          mediaToken(),
        ) ?? presetAvatarThumbUrl(resolvedImage, effectiveWidth))
      : null,
  );
  let thumbFailed = $state(false);
  let imgFailed = $state(false);
  // CYBORG-897 phase 4: the NON-thumb fallback used to be the raw S3 url — the
  // last step of the avatar path still addressing the object on the internet
  // rather than in our app. It is now the permanent /api/assets/o/<key> url too,
  // so "the thumbnailer couldn't serve it" degrades to a picture that still works
  // with the bucket closed — WITH A PASS ON IT. That is the case this fallback is
  // for, and it does work: /o/ authorizes and 302s to signed CloudFront.
  // CREDENTIAL-LESS it is not an image fallback at all — the relay 302s that caller
  // to the SPA's /media page so a HUMAN who pasted the link can sign in, and an
  // <img> cannot decode html. Don't read the line above as "this leg always paints".
  // Not-ours (preset art, data: uris) comes back unchanged.
  const fullSrc = $derived(
    resolvedImage
      ? avatarImageUrl(resolvedImage, client.httpBase || CLOUD_RELAY_HTTP_BASE, mediaToken())
      : resolvedImage,
  );
  const displaySrc = $derived(thumbFailed ? fullSrc : (thumbSrc ?? fullSrc));

  // Which leg is painting and whether it carries the media pass — never the url.
  function srcKind(): string {
    if (!displaySrc || imgFailed) return "none";
    return displaySrc === thumbSrc ? "thumb" : "raw";
  }
  function traceImg(event: string): void {
    if (!traceAs) return;
    coldStartTrace(event, {
      as: traceAs,
      kind: srcKind(),
      stored: !!resolvedImage,
      pass: !!displaySrc && /[?&]t=/.test(displaySrc),
    });
  }
  $effect(() => {
    if (!traceAs) return; // untraced avatars subscribe to nothing here
    // oxlint-disable-next-line eslint/no-unused-expressions -- trace every src change
    displaySrc;
    traceImg("avatar-src");
  });

  // Re-arm on the URLs we actually REQUEST, not just on the stored avatar string.
  //
  // The media pass is minted asynchronously and lands AFTER first paint, so an
  // avatar that painted before it arrived asked for both legs unauthenticated —
  // and with asset reads requiring a credential, both refuse an `<img>`: /api/assets/thumb
  // answers 401, and /api/assets/o/<key> bounces a credential-less caller to the
  // SPA's /media page (text/html, which an `<img>` cannot decode). Two onerrors,
  // so the glyph drops to initials — correct for that instant.
  //
  // Tracking only `resolvedImage` then LATCHED it there: the pass landing changes
  // `thumbSrc`/`fullSrc` but not the avatar string, so the flags never cleared and
  // the retry never happened. A surface mounted at app start (the sidebar) stayed
  // on initials for the life of the component while one mounted a moment later
  // (the chat header, the message list) painted the same photo fine — the reported
  // "one agent shows a letter in the DM list and his picture everywhere else".
  // Both are tracked because only `fullSrc` follows a bundled/data: avatar (whose
  // `thumbSrc` is a constant null) and only they carry the `?t=` pass.
  $effect(() => {
    // oxlint-disable-next-line eslint/no-unused-expressions -- track reactive dependency: a new thumb url (new avatar, or the media pass arriving) must retry
    thumbSrc;
    // oxlint-disable-next-line eslint/no-unused-expressions -- same for the non-thumb fallback url
    fullSrc;
    imgFailed = false;
    thumbFailed = false;
  });

  // Deterministic per-name swatch comes from the ONE shared util (utils.ts
  // nameToColor) so an identity gets the same color on every surface — see #528.
  const badgeSize = $derived(Math.max(10, Math.round(effectiveWidth * 0.35)));

  // Initials scale WITH the circle so a 2-letter monogram never overflows a
  // small avatar (e.g. size="sm" = 24px). An explicit numeric `fontSize` (the
  // workspace/DM/roster avatars) is honored verbatim, so those stay pixel-
  // identical; only the size-token/default callers that passed no fontSize get
  // the proportional value. Mirrors the emoji branch's effectiveWidth * 0.6.
  const initialsFontSize = $derived(fontSize ?? Math.round(effectiveWidth * 0.4));
</script>

<!-- Reusable presence dot, matching ChannelSidebar's dot style. -->
{#snippet statusDot()}
  {#if status}
    <span
      title={status === "online" ? "Active" : status === "error" ? "Error" : "Away"}
      class={cn(
        "absolute -bottom-0.5 -right-0.5 w-[8px] h-[8px] rounded-full border-[1.5px] border-avatar-ring",
        STATUS_COLORS[status],
      )}
    ></span>
  {/if}
{/snippet}

<!-- Connect organization mark: a small corner square carrying the partner org's initial.
     On the AVATAR and not beside the name on purpose — the picture is the one element
     every people surface already renders, so message rows, member lists, mention
     autocomplete, search results, DM rows and the profile card all inherit it from here.
     Same corner box as the overdue badge (badgeSize) so the two are visually one family;
     rounded-[3px] rather than a circle so it never reads as a presence dot. The swatch is
     the shared deterministic nameToColor, so one org is the same colour everywhere. -->
{#snippet orgMark()}
  {#if externalOrg}
    <span
      title={`External — ${externalOrg.name}`}
      aria-label={`External, from ${externalOrg.name}`}
      class="absolute -bottom-0.5 -right-0.5 flex items-center justify-center rounded-[3px] border-[1.5px] border-avatar-ring font-semibold uppercase leading-none text-accent-foreground"
      style:width="{badgeSize}px"
      style:height="{badgeSize}px"
      style:font-size="{Math.max(7, Math.round(badgeSize * 0.62))}px"
      style:background-color={nameToColor(externalOrg.name)}
    >
      <span aria-hidden="true">{firstGrapheme(externalOrg.name)}</span>
    </span>
  {/if}
{/snippet}

<!-- The avatar glyph itself (image | emoji | initials). One snippet so every
     wrapper context — overdue badge, presence dot, bare — renders the same
     three branches; `extraClass` carries the per-context className that was
     previously inlined on each leaf. Image + initials are byte-for-byte the
     legacy markup; emoji is the new branch, sized/rounded like initials. -->
{#snippet glyph(extraClass: string | undefined)}
  {#if displaySrc && !imgFailed}
    <img
      style:width="{effectiveWidth}px"
      style:height="{effectiveWidth}px"
      style:border-radius={typeof borderRadius === "number" ? `${borderRadius}px` : borderRadius}
      class={cn("overflow-hidden object-cover shrink-0", extraClass)}
      src={displaySrc}
      alt=""
      loading="lazy"
      decoding="async"
      onload={() => traceImg("avatar-load")}
      onerror={() => {
        traceImg("avatar-error");
        // Thumb failed → retry the raw URL; raw URL failed → glyph fallback.
        if (thumbSrc && !thumbFailed) thumbFailed = true;
        else imgFailed = true;
      }}
    />
  {:else if emoji}
    <div
      style:width="{effectiveWidth}px"
      style:height="{effectiveWidth}px"
      style:border-radius={typeof borderRadius === "number" ? `${borderRadius}px` : borderRadius}
      style:font-size="{Math.round(effectiveWidth * 0.6)}px"
      class={cn(
        "shrink-0 leading-none font-sans flex items-center justify-center bg-surface-alt select-none",
        extraClass,
      )}
    >
      <span aria-hidden="true">{emoji}</span>
    </div>
  {:else}
    <div
      style:width="{effectiveWidth}px"
      style:height="{effectiveWidth}px"
      style:border-radius={typeof borderRadius === "number" ? `${borderRadius}px` : borderRadius}
      style:font-size="{initialsFontSize}px"
      style:font-weight={fontWeight}
      style:background-color={nameToColor(name || "?")}
      class={cn(
        "shrink-0 uppercase font-sans flex items-center justify-center text-accent-foreground",
        extraClass,
      )}
    >
      <span class="select-none">{getInitials(name || "?")}</span>
    </div>
  {/if}
{/snippet}

{#if overdueWarning}
  <div class={cn("relative shrink-0", className)} style:width="{effectiveWidth}px" style:height="{effectiveWidth}px">
    {@render glyph(undefined)}
    <span
      title="Heartbeat overdue"
      class="absolute -bottom-0.5 -right-0.5 flex items-center justify-center rounded-full"
      style:width="{badgeSize}px"
      style:height="{badgeSize}px"
      style:background-color="var(--color-warning)"
      style:border="2px solid var(--bg-base)"
    >
      <svg width={Math.max(6, badgeSize - 6)} height={Math.max(6, badgeSize - 6)} viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M6 1.5L11 10.5H1L6 1.5Z" fill="var(--bg-base)" />
        <rect x="5.4" y="5" width="1.2" height="3" fill="var(--color-warning)" />
        <rect x="5.4" y="8.5" width="1.2" height="1.2" fill="var(--color-warning)" />
      </svg>
    </span>
  </div>
{:else if externalOrg}
  <!-- Relative wrapper for the org mark. It takes the bottom-right corner and the presence
       dot stands down for it: both want the same 8-12px of corner, and on someone from
       another company "which company" outranks "online vs away". A host member is
       unaffected — externalOrg is null for everyone but a Connect external. -->
  <div class={cn("relative shrink-0", className)} style:width="{effectiveWidth}px" style:height="{effectiveWidth}px">
    {@render glyph(undefined)}
    {@render orgMark()}
  </div>
{:else if status}
  <!-- Relative wrapper only when a presence dot needs to overlay. -->
  <div class={cn("relative shrink-0", className)} style:width="{effectiveWidth}px" style:height="{effectiveWidth}px">
    {@render glyph(undefined)}
    {@render statusDot()}
  </div>
{:else}
  {@render glyph(className)}
{/if}
